import { Divider, Grid2, TextField, Typography } from "@mui/material";
import { useEffect } from "react";
import {
	Control,
	Controller,
	UseFormSetValue,
	useWatch,
} from "react-hook-form";
import { CheckboxTextField } from "@/controls/CheckboxTextField";
import { TolerancePicker } from "@/controls/TolerancePicker";
import { Product } from "@/shared/models/Product";
import { PaintCutTypeSelector } from "@/shared/selectors/PaintCutTypeSelector";
import { parseNumberInput } from "@/shared/utils/parseNumberInput";
import { toFraction32 } from "@/utils/fraction";

type Props = {
	control: Control<Product>;
	setValue: UseFormSetValue<Product>;
	disabled: boolean;
};

const SectionHeader = ({ title }: { title: string }) => (
	<Grid2 size={{ xs: 12 }}>
		<Typography
			variant="subtitle2"
			fontWeight={600}
			color="primary"
			sx={{ mb: 0.5, mt: 1 }}
		>
			{title}
		</Typography>
		<Divider />
	</Grid2>
);

export const PaintSpecsFields = ({ control, setValue, disabled }: Props) => {
	const bundleCol = useWatch({ control, name: "BundleCol" });
	const bundleRow = useWatch({ control, name: "BundleRow" });
	// Web Holes is derived read-only ("Yes"/"No" from a Punch ancestor in the
	// lineage), consistent with the Products grid and Work Orders.
	const webHoles = useWatch({ control, name: "WebHoles" });

	useEffect(() => {
		const col = bundleCol ?? 0;
		const row = bundleRow ?? 0;
		const count = col && row ? col * row : null;
		setValue("BundleCount", count, { shouldDirty: true });
	}, [bundleCol, bundleRow, setValue]);

	return (
		<>
			<SectionHeader title="Paint Specs" />
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="FinishedLength"
					control={control}
					render={({ field }) => (
						<TextField
							label="Finished Length"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(parseNumberInput(e.target.value, { min: 0 }))
							}
							onBlur={() => {
								field.onBlur();
								if (typeof field.value === "number") {
									const r = Math.round(field.value * 1000) / 1000;
									if (r !== field.value) field.onChange(r);
								}
							}}
							fullWidth
							size="small"
							disabled={disabled}
							helperText={
								typeof field.value === "number"
									? toFraction32(field.value)
									: undefined
							}
							inputProps={{ min: 0, step: 0.001 }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 4 }}>
				<Controller
					name="Tolerance"
					control={control}
					render={({ field }) => (
						<TolerancePicker
							value={field.value}
							onChange={field.onChange}
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 2.5 }}>
				<CheckboxTextField
					label="Web Holes"
					checked={webHoles === "Yes"}
					onChange={() => {}}
					disabled
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 2.5 }}>
				<Controller
					name="PaintCutTypeId"
					control={control}
					render={({ field }) => (
						<PaintCutTypeSelector
							label="Sq. Cut / Or Clip"
							value={field.value}
							onChange={field.onChange}
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 6 }}>
				<Controller
					name="SpecialInstructions"
					control={control}
					render={({ field }) => (
						<TextField
							label="Special Instructions"
							value={field.value || ""}
							onChange={field.onChange}
							fullWidth
							size="small"
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>

			{/* Bundle Configuration */}
			<SectionHeader title="Bundle Configuration" />
			<Grid2 size={{ xs: 4, md: 1.7 }}>
				<Controller
					name="BundleCol"
					control={control}
					render={({ field }) => (
						<TextField
							label="Columns"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(
									parseNumberInput(e.target.value, { integer: true, min: 1 }),
								)
							}
							fullWidth
							size="small"
							disabled={disabled}
							inputProps={{ min: 1, step: 1 }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 4, md: 1.7 }}>
				<Controller
					name="BundleRow"
					control={control}
					render={({ field }) => (
						<TextField
							label="Rows"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(
									parseNumberInput(e.target.value, { integer: true, min: 1 }),
								)
							}
							fullWidth
							size="small"
							disabled={disabled}
							inputProps={{ min: 1, step: 1 }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 4, md: 2 }}>
				<Controller
					name="BundleCount"
					control={control}
					render={({ field }) => (
						<TextField
							label="Count"
							type="number"
							value={field.value ?? ""}
							fullWidth
							size="small"
							disabled
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 4, md: 2.5 }}>
				<Controller
					name="StickSize"
					control={control}
					render={({ field }) => (
						<TextField
							label="Stick Size"
							value={field.value || ""}
							onChange={field.onChange}
							fullWidth
							size="small"
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
		</>
	);
};
